import { memo, useEffect, useMemo, useRef, useState } from "react";

import { GET_BASE_URL_IMAGE } from "../constants/apiEndpoints";
import moment from "moment";
import StickerFairyQuickCreate from "./StickerFairyQuickCreate";
type StickerTabPropsType = {
  tabData: any;
  onClickSticker: any;
  onStickerInserted?: () => void;
};

export const getStickerProductImageUrl = (productImage: string) => {
  const value = String(productImage || "").trim();
  if (!value) {
    return "";
  }
  if (/^(https?:\/\/|data:image\/)/i.test(value)) {
    return value;
  }
  if (value.startsWith("/")) {
    return GET_BASE_URL_IMAGE + value;
  }
  return GET_BASE_URL_IMAGE + "/disneyland/images/products_thumbnail/" + value;
};

function Tab(props: any) {
  const getStickerName = (sticker: any): string =>
    String(sticker?.product_name || "").trim();
  const emojiData = props?.filter
    ? props?.allData
    : [...(props?.tab?.getemojidata || [])];
  const sortedCollection = emojiData.length
    ? [...emojiData].sort((a: any, b: any) => {
        if (props?.sortedBy === "name") {
          const aName = getStickerName(a);
          const bName = getStickerName(b);
          if (!aName && !bName) return 0;
          if (!aName) return 1;
          if (!bName) return -1;
          return aName.localeCompare(bName);
        }
        return moment.utc(b?.createdon).diff(moment.utc(a?.createdon));
      })
    : [];
  const filteredCollection = sortedCollection.filter((sticker: any) =>
    getStickerName(sticker)
      .toLowerCase()
      .includes(String(props?.filter || "").toLowerCase())
  );
  return (
    <div className="tab-content sticker-tabs-content">
      <div className="tab-pane fade active show sticker-tabs-pane">
        {filteredCollection.length === 0 ? (
          <p className="sticker-tabs-empty">No stickers found.</p>
        ) : (
          filteredCollection.map((item: any, index: any) => {
            const stickerKey = props.getStickerKey(item.product_image, item);
            const stickerName = getStickerName(item) || "sticker";
            return (
              <button
                key={index}
                type="button"
                className={`sticker-tabs-item ${
                  props.recentStickerKey === stickerKey ? "is-recently-inserted" : ""
                }`}
                aria-label={`Insert ${stickerName}`}
                onClick={() => props.onAddSticker(item.product_image, item)}
              >
                <img
                  src={getStickerProductImageUrl(item.product_image)}
                  className="img-fluid sticker-tabs-image"
                  alt={stickerName}
                />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function Navigation(props: any) {
  const isStickerFairyTab = (item: any) =>
    String(item?.emoji_category_name || "")
      .toLowerCase()
      .includes("sticker fairy");

  return (
    <div className="sticker-tabs-navigation">
      <div className="sticker-tabs-toolbar">
        <div className="sticker-tabs-search">
          <div className="input-group bg-white border rounded-pill p-1">
            <input
              placeholder="Search ..."
              aria-describedby="button-addon4"
              className="form-control border-0"
              value={props?.filter}
              onChange={(e) => props?.setFilter(e.target.value)}
            />
          </div>
        </div>
        <div className="sticker-tabs-sort">
          <h6 className="sticker-tabs-sort-label">Sorted By</h6>
          <div>
            <select
              className="form-select"
              aria-label="Sticker sort order"
              value={props?.sortedBy}
              onChange={(e) => {
                localStorage.setItem("sortedBy", e.target.value);
                props?.setSortedBy(e.target.value);
              }}
            >
              <option value={"name"}>Name</option>
              <option value={"date"}>Date</option>
            </select>
          </div>
        </div>
      </div>
      <div className="sticker-tabs-categories">
        <ul className="nav nav-pills sticker-tabs-nav">
          {props.tabs.map((item: any) => (
            <li key={item?.id} className={`nav-item`}>
              <span
                className={`nav-link sticker-tabs-nav-link ${
                  props.activeTabId === item?.id ? "active" : ""
                }`}
                onClick={() => props.onNavClick(item?.id)}
              >
                <span>{item.emoji_category_name}</span>
                {isStickerFairyTab(item) && (
                  <button
                    type="button"
                    className="sticker-fairy-quick-toggle"
                    aria-label="Create Sticker Fairy sticker"
                    aria-expanded={props.showStickerFairyQuickCreate}
                    onClick={(event) => {
                      event.stopPropagation();
                      props.setShowStickerFairyQuickCreate((open: boolean) => !open);
                      props.onNavClick(item?.id);
                    }}
                  >
                    +
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export const StickerTabs: React.FC<StickerTabPropsType> = ({
  tabData,
  onClickSticker,
  onStickerInserted,
}) => {
  const orderedTabs = useMemo(() => {
    const normalizedTabs = Array.isArray(tabData) ? tabData : [];
    const stickerFairyTabs = normalizedTabs.filter((tab: any) =>
      String(tab?.emoji_category_name || "")
        .toLowerCase()
        .includes("sticker fairy")
    );
    const otherTabs = normalizedTabs.filter(
      (tab: any) => !stickerFairyTabs.includes(tab)
    );
    return [...stickerFairyTabs, ...otherTabs];
  }, [tabData]);
  const [activeTabId, setActiveTab] = useState(orderedTabs[0]?.id);
  const [filter, setFilter] = useState("");
  const [showStickerFairyQuickCreate, setShowStickerFairyQuickCreate] = useState(false);
  const [insertedCount, setInsertedCount] = useState(0);
  const [recentStickerKey, setRecentStickerKey] = useState("");
  const feedbackTimerRef = useRef<number | null>(null);
  const [sortedBy, setSortedBy] = useState(
    localStorage.getItem("sortedBy") || "name"
  );
  const showInsertFeedback = Boolean(onStickerInserted);

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current !== null) {
        window.clearTimeout(feedbackTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      orderedTabs.length > 0 &&
      !orderedTabs.some((tab: any) => tab?.id === activeTabId)
    ) {
      setActiveTab(orderedTabs[0]?.id);
    }
  }, [activeTabId, orderedTabs]);
  const activeTab = useMemo(
    () => orderedTabs.find((tab: any) => tab?.id === activeTabId),
    [activeTabId, orderedTabs]
  );
  const allData = useMemo(() => {
    return orderedTabs.flatMap((item: any) => item?.getemojidata || []);
  }, [orderedTabs]);
  const getStickerKey = (productImage: string, item?: any) =>
    String(item?.product_id || productImage || "");
  const notifyStickerInserted = () => {
    if (!onStickerInserted) {
      return;
    }

    requestAnimationFrame(() => onStickerInserted());
  };
  const showStickerInsertedFeedback = (productImage: string, item?: any) => {
    if (!showInsertFeedback) {
      return;
    }

    const stickerKey = getStickerKey(productImage, item);
    setInsertedCount((count) => count + 1);
    setRecentStickerKey(stickerKey);

    if (feedbackTimerRef.current !== null) {
      window.clearTimeout(feedbackTimerRef.current);
    }
    feedbackTimerRef.current = window.setTimeout(() => {
      setRecentStickerKey((currentKey) =>
        currentKey === stickerKey ? "" : currentKey
      );
      feedbackTimerRef.current = null;
    }, 1200);
  };

  const insertStickerProductImage = (
    productImage: string,
    item?: any,
    closeAfterInsert = false
  ) => {
    if (!productImage) {
      return;
    }

    onClickSticker(getStickerProductImageUrl(productImage), item);
    showStickerInsertedFeedback(productImage, item);

    if (closeAfterInsert) {
      notifyStickerInserted();
    }
  };

  const onQuickCreateStickerReady = (productImage: string, item?: any) => {
    insertStickerProductImage(productImage, item, true);
    setShowStickerFairyQuickCreate(false);
  };

  return (
    <div className="com-box-main sticker-tabs-panel">
      <div className="post-sec">
        <div className="tabs">
          <Navigation
            tabs={orderedTabs}
            onNavClick={setActiveTab}
            activeTabId={activeTabId}
            showStickerFairyQuickCreate={showStickerFairyQuickCreate}
            setShowStickerFairyQuickCreate={setShowStickerFairyQuickCreate}
            setSortedBy={setSortedBy}
            sortedBy={sortedBy}
            setFilter={setFilter}
            filter={filter}
          />
          {showStickerFairyQuickCreate && (
            <StickerFairyQuickCreate
              stickerData={orderedTabs}
              onStickerReady={onQuickCreateStickerReady}
            />
          )}
          <Tab
            tab={activeTab}
            onAddSticker={insertStickerProductImage}
            sortedBy={sortedBy}
            filter={filter}
            allData={allData}
            getStickerKey={getStickerKey}
            recentStickerKey={recentStickerKey}
          />
          {showInsertFeedback && (
            <div className="sticker-tabs-feedback" aria-live="polite">
              <span className="sticker-tabs-feedback-status">
                {insertedCount === 0
                  ? "No stickers inserted yet"
                  : `${insertedCount} sticker${insertedCount === 1 ? "" : "s"} inserted`}
              </span>
              <button
                type="button"
                className="sticker-tabs-done"
                onClick={notifyStickerInserted}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(StickerTabs);
